import React, { useCallback, useEffect, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation } from 'react-router-dom';
import IssueService from '../../services/IssueService/IssueService';
import Issue from '../../types/Issue';
import PageTitleSection from '../../components/PageTitleSection/PageTitleSection';
import PageContentContainer from '../../components/PageContentContainer/PageContentContainer';
import SearchBar from '../../components/SearchBar/SearchBar';
import DataTable from '../../components/DataTable/DataTable';
import { HeaderDefinition } from '../../components/DataTable/DataTableHeader';
import { TableCell } from '../../components/ui/table';
import useAuthRoles from '../../hooks/useAuthRoles';
import IssueDetailModal from './IssueDetailModal/IssueDetailModal';

type IssueSortColumn =
  | 'reference'
  | 'title'
  | 'scope'
  | 'status'
  | 'createdAt'
  | 'lastTaggedAt'
  | 'resolvedAt';

const formatIssueDate = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
};

function IssuesPage(): React.ReactElement {
  const { userIsJapetoAdmin } = useAuthRoles();
  const location = useLocation();

  const [query, setQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [searchData, setSearchData] = useState<Issue[]>();
  const [searchIsLoading, setSearchIsLoading] = useState<boolean>(true);
  const [resultsPerPage, setResultsPerPage] = useState<number>(25);
  const [hasError, setHasError] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState<IssueSortColumn>('reference');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [showSuccessSnackbar, setShowSuccessSnackbar] =
    useState<boolean>(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const openSuccessSnackbar = (message: string): void => {
    setSnackbarMessage(message);
    setShowErrorSnackbar(false);
    setShowSuccessSnackbar(true);
  };
  const openErrorSnackbar = (message: string): void => {
    setSnackbarMessage(message);
    setShowSuccessSnackbar(false);
    setShowErrorSnackbar(true);
  };

  const getIssues = useCallback(
    (signal?: AbortSignal): void => {
      setSearchIsLoading(true);
      IssueService.listIssues({
        query,
        page,
        resultsPerPage,
        sortColumn,
        sortDirection,
        signal
      })
        .then((data) => {
          setHasError(false);
          setNumberOfResults(data.numberOfResults);
          setSearchData(data.results);
        })
        .catch((err) => {
          if (err.name === 'CanceledError') return;
          setNumberOfResults(0);
          setSearchData([]);
          setHasError(true);
        })
        .finally(() => setSearchIsLoading(false));
    },
    [query, page, resultsPerPage, sortColumn, sortDirection]
  );

  useEffect(() => {
    const controller = new AbortController();
    getIssues(controller.signal);
    return () => controller.abort();
  }, [getIssues]);

  const handleSortChange = useCallback(() => {
    getIssues();
  }, [getIssues]);

  if (!userIsJapetoAdmin()) {
    return <Navigate to='/chats' state={{ from: location }} replace />;
  }

  const issueHeaders: HeaderDefinition[] = [
    {
      key: 'reference',
      label: 'Issue',
      sortable: true,
      className: 'w-[9%]'
    },
    { key: 'title', label: 'Title', sortable: true, className: 'w-[16%]' },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      className: 'w-[20%]'
    },
    { key: 'scope', label: 'Scope', sortable: true, className: 'w-[9%]' },
    { key: 'status', label: 'Status', sortable: true, className: 'w-[9%]' },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      className: 'w-[12%]'
    },
    {
      key: 'lastTaggedAt',
      label: 'Last tagged',
      sortable: true,
      className: 'w-[13%]'
    },
    {
      key: 'resolvedAt',
      label: 'Resolved',
      sortable: true,
      className: 'w-[12%]'
    }
  ];

  const renderIssueRow = (issue: Issue): React.ReactElement => (
    <>
      <TableCell className='w-[9%]'>
        <span className='block pr-2 font-medium'>{issue.reference}</span>
      </TableCell>
      <TableCell className='w-[16%]'>
        <span
          className='text-text-secondary block truncate pr-2'
          title={issue.title ?? ''}
        >
          {issue.title || '—'}
        </span>
      </TableCell>
      <TableCell className='w-[20%]'>
        <span
          className='text-text-secondary block truncate pr-2'
          title={issue.description ?? ''}
        >
          {issue.description || '—'}
        </span>
      </TableCell>
      <TableCell className='w-[9%]'>
        {issue.scope === 'GLOBAL' ? 'Global' : 'Chatbot'}
      </TableCell>
      <TableCell className='w-[9%]'>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            issue.status === 'RESOLVED'
              ? 'bg-green-500/10 text-green-700'
              : 'bg-brand-light/10 text-brand-light'
          }`}
        >
          {issue.status === 'RESOLVED' ? 'Resolved' : 'Open'}
        </span>
      </TableCell>
      <TableCell className='w-[12%]'>
        {formatIssueDate(issue.createdAt)}
      </TableCell>
      <TableCell className='w-[13%]'>
        {formatIssueDate(issue.lastTaggedAt)}
      </TableCell>
      <TableCell className='w-[12%]'>
        {formatIssueDate(issue.resolvedAt)}
      </TableCell>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Japeto - Issues</title>
      </Helmet>
      <PageTitleSection title='Issues' />
      <PageContentContainer>
        <SearchBar
          setQuery={setQuery}
          searchTermFromState={query}
          placeholder='Search issues by reference, title or description...'
        />
        <DataTable<Issue>
          searchData={searchData || null}
          numberOfResults={numberOfResults}
          resultsPerPage={resultsPerPage}
          isLoading={searchIsLoading}
          setResultsPerPage={setResultsPerPage}
          setPage={setPage}
          page={page}
          tableHeaders={issueHeaders}
          renderRow={renderIssueRow}
          sortColumn={sortColumn}
          setSortColumn={
            setSortColumn as React.Dispatch<React.SetStateAction<string>>
          }
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onSortChange={handleSortChange}
          hasError={hasError}
          handleFetch={getIssues}
          noDataTitle='No issues found'
          noDataMessage="There aren't any issues matching your search."
          isClickable
          handleRowClick={(issue) => {
            setSelectedIssue(issue);
            setModalOpen(true);
          }}
        />
      </PageContentContainer>

      <IssueDetailModal
        open={modalOpen}
        issue={selectedIssue}
        onClose={() => setModalOpen(false)}
        onUpdated={getIssues}
        openSuccessSnackbar={openSuccessSnackbar}
        openErrorSnackbar={openErrorSnackbar}
      />

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity='success'
          variant='filled'
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity='error'
          variant='filled'
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default IssuesPage;
